import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import Checkbox from 'Components/Atoms/Checkbox';
import Add from '@material-ui/icons/Add';

/** From this many entries a list gets a search field and "All on / All off". */
const LIST_TOOLS_FROM = 6;

type Entry = { id: string; title: string };

type CheckListProps = {
  label: string;
  entries: Entry[];
  selected: string[];
  onToggle: (id: string) => void;
  /** Switches all given entries on or off at once (the ones matching the search). */
  onSetAll: (ids: string[], on: boolean) => void;
};

const CheckList = ({ label, entries, selected, onToggle, onSetAll }: CheckListProps) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const term = search.trim().toLocaleLowerCase();
  const shown = term
    ? entries.filter((entry) => entry.title.toLocaleLowerCase().includes(term))
    : entries;
  const shownIds = shown.map((entry) => entry.id);
  const tools = entries.length >= LIST_TOOLS_FROM;

  const selectedCount = entries.filter((entry) => selected.includes(entry.id)).length;

  return (
    <div className="occ-spaces__group">
      <div className="occ-spaces__head">
        <Typography color="subdued" variant="text-4">
          {label}
        </Typography>
        {tools && (
          <span className="occ-spaces__count">
            {t('{{selected}} of {{total}}', { selected: selectedCount, total: entries.length })}
          </span>
        )}
        {tools && (
          <span className="occ-spaces__actions">
            <button
              type="button"
              className="occ-spaces__action"
              disabled={shownIds.every((id) => selected.includes(id))}
              onClick={() => onSetAll(shownIds, true)}
            >
              {t('experiences_All on')}
            </button>
            <button
              type="button"
              className="occ-spaces__action"
              disabled={!shownIds.some((id) => selected.includes(id))}
              onClick={() => onSetAll(shownIds, false)}
            >
              {t('experiences_All off')}
            </button>
          </span>
        )}
      </div>
      {tools && (
        <input
          type="search"
          className="occ-spaces__search"
          placeholder={t('Search...')}
          aria-label={`${t(label)}: ${t('Search...')}`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      <div className={tools ? 'occ-spaces__list is-scrollable' : 'occ-spaces__list'}>
        {shown.map((entry) => (
          <Checkbox
            key={entry.id}
            checked={selected.includes(entry.id)}
            onChange={() => onToggle(entry.id)}
            label={entry.title}
            color="primary"
          />
        ))}
        {!shown.length && (
          <Typography color="subdued" variant="text-4" translation={null} className="occ-spaces__empty">
            {t('No matches for “{{term}}”', { term: search.trim() })}
          </Typography>
        )}
      </div>
    </div>
  );
};

type State = {
  spaces: string[];
  occassions: string[];
  matchBoth?: boolean; // matchBoth = TRUE => and
};

export type OccassionsAndSpacesPopoverProps = {
  state: State;
  onChange: (newState: State) => void;
  occassions: { id: string; title: string; hidden?: boolean }[];
  spaces: { id: string; title: string }[];
  stateOccassions: any;
  stateSpaces: any;
  onAddOccassion?: () => void;
};

const OccassionsAndSpacesPopover: React.FC<OccassionsAndSpacesPopoverProps> = ({
  state,
  occassions,
  spaces,
  onChange,
  stateOccassions,
  stateSpaces,
  onAddOccassion,
}) => {
  const handleChangeOcc = (id: any) => {
    let newOccassions: string[] = [];
    stateOccassions.length === 0 && newOccassions.push(id);
    stateOccassions.forEach((o: string) => {
      if (o === id) {
        newOccassions = stateOccassions.filter((elem: string) => elem !== id);
      } else if (stateOccassions.indexOf(o) === 0) {
        newOccassions = [...stateOccassions];
        !newOccassions.includes(id) && newOccassions.push(id);
      }
    });

    onChange({
      ...state,
      occassions: newOccassions.filter((o) =>
        occassions.find((occ) => occ.id === o)
      ),
    });
  };

  const handleChangeSpaces = (id: any) => {
    let newSpaces: string[] = [];
    stateSpaces.length === 0 && newSpaces.push(id);
    stateSpaces.forEach((o: string) => {
      if (o === id) {
        newSpaces = stateSpaces.filter((elem: string) => elem !== id);
      } else if (stateSpaces.indexOf(o) === 0) {
        newSpaces = [...stateSpaces];
        !newSpaces.includes(id) && newSpaces.push(id);
      }
    });
    onChange({
      ...state,
      spaces: newSpaces.filter((s) => spaces.find((sp) => sp.id === s)),
    });
  };

  const setAll = (key: 'occassions' | 'spaces', ids: string[], on: boolean) => {
    const known = key === 'occassions' ? occassions : spaces;
    const current: string[] = key === 'occassions' ? stateOccassions : stateSpaces;
    const next = on
      ? [...current, ...ids.filter((id) => !current.includes(id))]
      : current.filter((id) => !ids.includes(id));
    onChange({ ...state, [key]: next.filter((id) => known.some((entry) => entry.id === id)) });
  };

  useEffect(() => {
    if (
      state.occassions.length + state.spaces.length === 1 &&
      state.matchBoth
    ) {
      onChange({
        ...state,
        matchBoth: false,
      });
    }
  }, [state]);

  return (
    <>
      <Box>
        <CheckList
          label="Occassions"
          entries={occassions}
          selected={stateOccassions}
          onToggle={handleChangeOcc}
          onSetAll={(ids, on) => setAll('occassions', ids, on)}
        />
        {!!onAddOccassion && (
          <Button
            onClick={onAddOccassion}
            style={{
              background: 'var(--color-btn-default)',
              padding: '4px 10px',
              fontSize: '14px',
              lineHeight: '19.6px',
              color: 'var(--color-grey-9)',
            }}
            endIcon={(p) => <Add {...p} />}
            translation="calendar"
          >
            Add Occassion
          </Button>
        )}
      </Box>

      <Box>
        <CheckList
          label="Spaces"
          entries={spaces}
          selected={stateSpaces}
          onToggle={handleChangeSpaces}
          onSetAll={(ids, on) => setAll('spaces', ids, on)}
        />
      </Box>

      {!!stateSpaces.length && !!stateOccassions.length && (
        <>
          <Typography
            color="subdued"
            variant="text-4"
            style={{ marginBottom: 4 }}
            translation="settings"
          >
            Match Space and/or Occassion
          </Typography>
          <Box flex style={{ marginBottom: 4 }}>
            <Button
              variant={state.matchBoth === false ? 'primary' : 'default'}
              style={{ padding: 4, borderRadius: 4, marginRight: 8 }}
              onClick={() => onChange({ ...state, matchBoth: false })}
              translation="calendar"
            >
              Space or Occassion
            </Button>

            <Button
              variant={state.matchBoth === true ? 'primary' : 'default'}
              style={{ padding: 4, borderRadius: 4 }}
              onClick={() => onChange({ ...state, matchBoth: true })}
              translation="calendar"
            >
              Space & Occassion
            </Button>
          </Box>
          <Typography color="subdued" variant="text-4" translation="calendar">
            Should both Space and Occassion match for a table. Or only one of
            them
          </Typography>
        </>
      )}
    </>
  );
};

export default OccassionsAndSpacesPopover;
